/**
 * 智能运镜轨迹生成 —— 方向感知路径简化 + 圆润平滑
 *
 * 相比 trackFromColmap 的 Catmull-Rom 平滑 + 等弧长重采样（保留原始路径拓扑），
 * 本算法通过折返检测自动消除回程，并做圆润处理避免锐角转弯：
 *
 * 1. 层高规整 — 同层高度压到中位数，跨层连接段保留
 * 2. RDP 简化 — 提取关键航点，去除高频抖动
 * 3. 折返检测 — 检测路径折返到已访问区域，跳过回程段
 * 4. 最少关键帧保障 — 去重后点数不足时回退到 RDP 结果
 * 5. 高密度补帧 — Catmull-Rom 插值生成密集路径点
 * 6. 锐角检测与切角 — 在急转弯处用 Catmull-Rom 替换锐角段
 * 7. 迭代平滑 — 多轮移动平均 + 等弧长重采样到密集关键帧
 * 8. 长段补帧 — 对水平距离过长的相邻点插入中间帧
 * 9. 朝向构建 — 每个点看向路径前方固定距离
 * 10. 时间分配 — 按路径长度匀速分配时间
 */
import { Vec3 } from '../vector';
import type { Track } from '../types';
import type { Colmap } from './colmap';
import { calculateColmapFov, cameraPositionFromQvecTvec } from './transform';
import type { CameraIntrinsics, ColmapCameraData } from './types';
import { compareImageNames } from './utils';

const DEFAULT_FOV = 60;
const DEFAULT_TARGET_DISTANCE = 10;
const DEFAULT_DURATION_PER_KEYFRAME = 1.5;
const DEFAULT_MOTION_SPEED = 1;
const DEFAULT_TURN_COST_WEIGHT = 0;

export interface SmartTrackOptions {
  cameraId?: number;
  name?: string;
  fov?: number;
  targetDistance?: number;
  durationPerKeyframe?: number;
  /** RDP 简化阈值，相对于路径包围盒对角线的比例，默认 0.01 */
  rdpEpsilonRatio?: number;
  /** 折返检测阈值，相对于包围盒对角线的比例，默认 0.05 */
  backtrackDistRatio?: number;
  /** 等弧长重采样目标点数，越多越平滑，默认 120 */
  targetKeyframes?: number;
  /** 最少关键帧数，低于此值回退到 RDP 结果，默认 8 */
  minKeyframes?: number;
  /** 锐角阈值（度），相邻段夹角大于此值时做切角处理，默认 60 */
  sharpAngleThreshold?: number;
  /** 切角单侧影响点数，默认 3 */
  cornerSmoothingPoints?: number;
  /** 迭代平滑轮数，默认 3 */
  smoothingIterations?: number;
  /** 是否启用层高规整，默认 true */
  enableFloorHeightNormalize?: boolean;
  /** 同层高度带宽（米），默认 2 */
  floorBandHeight?: number;
  /** 判定跨层连接的高度差（米），默认 2 */
  heightTransitionThreshold?: number;
  /** 构成稳定楼层的最少点数，默认 5 */
  minFloorPoints?: number;
  /** 高度预平滑窗口，默认 3 */
  heightSmoothWindow?: number;
  /** 楼层切换边界保留点数，默认 2 */
  heightTransitionPadding?: number;
  /** 单段最大水平距离（米），超过则插入中间帧，默认 1.5 */
  maxSegmentDistance?: number;
  /** 运镜速度，单位为米/秒，默认 1 */
  motionSpeed?: number;
  /** 转向成本权重，0 表示按路径距离匀速，越大转角越慢，默认 0 */
  turnCostWeight?: number;
  /** 关键帧之间插值平滑度，1=Catmull-Rom 样条（近似匀速），0=ease-in-out（每点停顿），默认 1 */
  smoothness?: number;
}

export function smartTrackFromColmap(
  colmap: Colmap,
  options: SmartTrackOptions = {},
): Track | null {
  const imageGroup = selectImageGroup(
    groupImagesByCameraId(colmap.images, colmap.cameras),
    options.cameraId,
  );
  if (!imageGroup) return null;

  const camera = imageGroup.camera;
  const rawPositions = imageGroup.images.map((img) => {
    const qvec = [img.qw, img.qx, img.qy, img.qz];
    return cameraPositionFromQvecTvec(qvec, [img.tx, img.ty, img.tz]);
  });

  if (rawPositions.length < 2) return null;

  // 过滤异常离群点（COLMAP 解算失败导致的极端坐标）
  const filtered = filterPositionOutliers(rawPositions);
  if (filtered.length < 2) return null;

  const leveled =
    options.enableFloorHeightNormalize === false
      ? filtered
      : normalizeFloorHeights(filtered, {
          floorBandHeight: options.floorBandHeight ?? 2,
          heightTransitionThreshold: options.heightTransitionThreshold ?? 2,
          minFloorPoints: options.minFloorPoints ?? 5,
          heightSmoothWindow: options.heightSmoothWindow ?? 3,
          transitionPadding: options.heightTransitionPadding ?? 2,
        });

  // 计算包围盒，用于自适应阈值
  const bounds = computeBounds(leveled);
  const diagonal = Math.sqrt(bounds.size.x * bounds.size.x + bounds.size.z * bounds.size.z);
  if (diagonal < 1e-6) return null;

  const rdpEpsilon = (options.rdpEpsilonRatio ?? 0.01) * diagonal;
  const backtrackDist = (options.backtrackDistRatio ?? 0.05) * diagonal;
  const targetKf = options.targetKeyframes ?? 120;
  const minKf = options.minKeyframes ?? 8;

  // Step 1: RDP 简化 — 去除高频抖动
  const simplified = rdpSimplify(leveled, rdpEpsilon);

  // Step 2: 折返检测 — 跳过回到已访问区域的回程段
  const deduped = removeBacktracking(simplified, backtrackDist);

  // Step 3: 最少关键帧保障 — 去重后点数不足则回退到 RDP 结果
  const cleaned = deduped.length >= minKf ? deduped : simplified;

  // Step 4: 高密度补帧 — Catmull-Rom 插值生成密集路径点
  const dense = catmullRomDensify(cleaned, 12);

  // Step 5: 锐角检测与切角 — 在急转弯处插入过渡点，消除锐角
  const sharpAngle = options.sharpAngleThreshold ?? 60;
  const cornerPoints = options.cornerSmoothingPoints ?? 3;
  const cornered = smoothCorners(dense, sharpAngle, cornerPoints);

  // Step 6: 迭代平滑 — 多轮移动平均消除残余抖动
  const smoothIterations = options.smoothingIterations ?? 3;
  const smoothed = iterativeSmooth(cornered, smoothIterations, targetKf);

  // Step 7: 长段补帧 — 对水平距离过长的相邻点插入中间帧
  const maxSegmentDistance = options.maxSegmentDistance ?? 1.5;
  const trackPositions = insertDistanceFrames(smoothed, maxSegmentDistance);

  // Step 8: 构建 Track — 沿路径 look-ahead 分配目标点，按路径长度匀速分配时间
  const targetDistance = options.targetDistance ?? DEFAULT_TARGET_DISTANCE;
  const defaultFov = camera ? calculateColmapFov(camera) : (options.fov ?? DEFAULT_FOV);
  const durationPerKf = options.durationPerKeyframe ?? DEFAULT_DURATION_PER_KEYFRAME;
  const motionSpeed = Math.max(options.motionSpeed ?? DEFAULT_MOTION_SPEED, 1e-6);
  const turnCostWeight = Math.max(options.turnCostWeight ?? DEFAULT_TURN_COST_WEIGHT, 0);

  // 按路径长度匀速分配时间，turnCostWeight=0 时整段为匀速运动。
  const cumCost = [0];
  for (let i = 1; i < trackPositions.length; i++) {
    cumCost.push(
      cumCost[i - 1] + segmentMotionCost(trackPositions, i - 1, targetDistance, turnCostWeight),
    );
  }
  const totalCost = cumCost[cumCost.length - 1];
  const totalDuration =
    totalCost > 1e-6 ? totalCost / motionSpeed : (trackPositions.length - 1) * durationPerKf;

  const times: number[] = [];
  const position: number[] = [];
  const target: number[] = [];
  const fov: number[] = [];

  for (let i = 0; i < trackPositions.length; i++) {
    const pos = trackPositions[i];
    const targetPoint = lookAheadTarget(trackPositions, i, targetDistance);

    times.push(totalCost > 1e-6 ? (cumCost[i] / totalCost) * totalDuration : i * durationPerKf);
    position.push(pos.x, pos.y, pos.z);
    target.push(targetPoint.x, targetPoint.y, targetPoint.z);
    fov.push(defaultFov);
  }

  return {
    name: options.name ?? 'smart-track',
    duration: totalDuration,
    loopMode: 'repeat',
    // 开放路径使用非循环 spline 分支，避免 looping tangent 造成段内速度起伏。
    interpolation: 'step',
    // smoothness 1 配合非循环 spline + 等弧长时间分配，近似匀速且段间连续；
    // 0 会让每点速度归零（ease-in-out），产生停-冲-停-冲的体感。
    smoothness: options.smoothness ?? 1,
    keyframes: {
      times,
      values: { position, target, fov },
    },
  };
}

// ─── RDP (Ramer-Douglas-Peucker) 简化 ─────────────────────────────────────────

function rdpSimplify(points: Vec3[], epsilon: number): Vec3[] {
  if (points.length < 3) return points.slice();

  const keep = new Array(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;

  const stack: Array<[number, number]> = [[0, points.length - 1]];
  while (stack.length > 0) {
    const [start, end] = stack.pop()!;
    let maxDist = 0;
    let maxIdx = -1;
    for (let i = start + 1; i < end; i++) {
      const d = pointLineDistanceXZ(points[i], points[start], points[end]);
      if (d > maxDist) {
        maxDist = d;
        maxIdx = i;
      }
    }
    if (maxDist > epsilon && maxIdx >= 0) {
      keep[maxIdx] = true;
      stack.push([start, maxIdx]);
      stack.push([maxIdx, end]);
    }
  }

  return points.filter((_, i) => keep[i]);
}

function distanceXZ(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dz * dz);
}

function directionXZ(from: Vec3, to: Vec3): Vec3 {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const len = Math.sqrt(dx * dx + dz * dz);
  if (len < 1e-10) return Vec3.FORWARD;
  return new Vec3(dx / len, 0, dz / len);
}

function pointLineDistanceXZ(p: Vec3, a: Vec3, b: Vec3): number {
  const abx = b.x - a.x;
  const abz = b.z - a.z;
  const abLenSq = abx * abx + abz * abz;
  if (abLenSq < 1e-10) return distanceXZ(p, a);
  const apx = p.x - a.x;
  const apz = p.z - a.z;
  const t = Math.max(0, Math.min(1, (apx * abx + apz * abz) / abLenSq));
  const projX = a.x + abx * t;
  const projZ = a.z + abz * t;
  const dx = p.x - projX;
  const dz = p.z - projZ;
  return Math.sqrt(dx * dx + dz * dz);
}

// ─── 折返检测 — 跳过回到已访问区域的回程段 ──────────────────────────────────────

function removeBacktracking(points: Vec3[], minDist: number): Vec3[] {
  if (points.length === 0) return [];
  if (points.length < 3) return points.slice();

  const result: Vec3[] = [points[0]];

  for (let i = 1; i < points.length; i++) {
    const p = points[i];
    let isRevisit = false;
    for (const kept of result) {
      if (distanceXZ(p, kept) < minDist) {
        isRevisit = true;
        break;
      }
    }
    if (!isRevisit) {
      result.push(p);
    }
  }

  const last = points[points.length - 1];
  if (result.length > 0 && result[result.length - 1] !== last) {
    let isLastNovel = true;
    for (const kept of result) {
      if (distanceXZ(last, kept) < minDist) {
        isLastNovel = false;
        break;
      }
    }
    if (isLastNovel) result.push(last);
  }

  return result;
}

// ─── 高密度补帧 ────────────────────────────────────────────────────────────────

function catmullRomDensify(points: Vec3[], density: number): Vec3[] {
  if (points.length < 3) return points.slice();

  const result: Vec3[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    for (let j = 0; j < density; j++) {
      result.push(catmullRomPoint(p0, p1, p2, p3, j / density));
    }
  }
  result.push(points[points.length - 1]);
  return result;
}

// ─── 锐角检测与切角 ──────────────────────────────────────────────────────────────

function smoothCorners(points: Vec3[], angleThresholdDeg: number, insertCount: number): Vec3[] {
  if (points.length < 5) return points;

  const threshold = (angleThresholdDeg * Math.PI) / 180;
  const result: Vec3[] = [];
  const n = points.length;

  let i = 0;
  while (i < n) {
    if (i === 0 || i === n - 1) {
      result.push(points[i]);
      i++;
      continue;
    }

    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];

    const dirIn = directionXZ(prev, curr);
    const dirOut = directionXZ(curr, next);
    const dot = Math.max(-1, Math.min(1, dirIn.dot(dirOut)));
    const angle = Math.acos(dot);

    if (angle > threshold) {
      const span = Math.min(insertCount, i, n - 1 - i);
      if (span < 1) {
        result.push(points[i]);
        i++;
        continue;
      }

      for (let k = 0; k < span - 1 && result.length > 0; k++) {
        result.pop();
      }

      const a = points[Math.max(0, i - span - 1)];
      const b = points[i - span];
      const c = points[i + span];
      const d = points[Math.min(n - 1, i + span + 1)];

      const steps = span * 2 + 1;
      for (let j = 0; j < steps; j++) {
        const t = j / (steps - 1);
        result.push(catmullRomPoint(a, b, c, d, t));
      }

      i += span + 1;
    } else {
      result.push(points[i]);
      i++;
    }
  }

  return result;
}

// ─── 迭代平滑 ──────────────────────────────────────────────────────────────────

function iterativeSmooth(points: Vec3[], iterations: number, targetCount: number): Vec3[] {
  if (points.length < 3) return points;

  let current = points;
  for (let iter = 0; iter < iterations; iter++) {
    const next: Vec3[] = [current[0]];
    for (let i = 1; i < current.length - 1; i++) {
      const p0 = current[i - 1];
      const p1 = current[i];
      const p2 = current[i + 1];
      next.push(
        new Vec3(
          p0.x * 0.25 + p1.x * 0.5 + p2.x * 0.25,
          p0.y * 0.25 + p1.y * 0.5 + p2.y * 0.25,
          p0.z * 0.25 + p1.z * 0.5 + p2.z * 0.25,
        ),
      );
    }
    next.push(current[current.length - 1]);
    current = next;
  }

  return arcLengthResample(current, targetCount);
}

function arcLengthResample(points: Vec3[], count: number): Vec3[] {
  if (points.length < 2 || count < 2) return points.slice(0, Math.max(count, 1));

  const cumLen = [0];
  for (let i = 1; i < points.length; i++) {
    cumLen.push(cumLen[i - 1] + distanceXZ(points[i], points[i - 1]));
  }
  const totalLen = cumLen[cumLen.length - 1];
  if (totalLen <= 1e-6) return points.slice(0, count);

  const result: Vec3[] = [];
  let seg = 0;
  for (let i = 0; i < count; i++) {
    const targetDist = (i / (count - 1)) * totalLen;
    while (seg < cumLen.length - 2 && cumLen[seg + 1] < targetDist) seg++;
    const segLen = cumLen[seg + 1] - cumLen[seg];
    const t = segLen > 1e-10 ? (targetDist - cumLen[seg]) / segLen : 0;
    const a = points[seg];
    const b = points[Math.min(seg + 1, points.length - 1)];
    result.push(new Vec3(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, a.z + (b.z - a.z) * t));
  }
  return result;
}

function catmullRomPoint(p0: Vec3, p1: Vec3, p2: Vec3, p3: Vec3, t: number): Vec3 {
  const t2 = t * t;
  const t3 = t2 * t;
  return new Vec3(
    0.5 *
      (2 * p1.x +
        (-p0.x + p2.x) * t +
        (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
        (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
    0.5 *
      (2 * p1.y +
        (-p0.y + p2.y) * t +
        (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
        (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
    0.5 *
      (2 * p1.z +
        (-p0.z + p2.z) * t +
        (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t2 +
        (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t3),
  );
}

// ─── 工具函数 ──────────────────────────────────────────────────────────────────

function insertDistanceFrames(points: Vec3[], maxSegmentDistance: number): Vec3[] {
  if (points.length < 2 || maxSegmentDistance <= 0) return points;
  const result: Vec3[] = [points[0]];
  for (let i = 0; i < points.length - 1; i++) {
    const from = points[i];
    const to = points[i + 1];
    const distance = distanceXZ(from, to);
    const insertCount = Math.max(0, Math.ceil(distance / maxSegmentDistance) - 1);
    for (let j = 1; j <= insertCount; j++) {
      const t = j / (insertCount + 1);
      result.push(lerpVec3(from, to, t));
    }
    result.push(to);
  }
  return result;
}

function segmentMotionCost(
  points: Vec3[],
  segmentStart: number,
  targetDistance: number,
  turnCostWeight: number,
): number {
  const from = points[segmentStart];
  const to = points[Math.min(segmentStart + 1, points.length - 1)];
  const moveCost = distanceXZ(from, to);
  if (turnCostWeight <= 0) return Math.max(moveCost, 1e-6);
  const fromForward = forwardToNext(points, segmentStart);
  const toForward = forwardToNext(points, Math.min(segmentStart + 1, points.length - 1));
  const turnAngle = Math.acos(Math.max(-1, Math.min(1, fromForward.dot(toForward))));
  const turnCost = targetDistance * turnAngle * turnCostWeight;
  return Math.max(moveCost + turnCost, 1e-6);
}

function forwardToNext(points: Vec3[], index: number): Vec3 {
  if (points.length < 2) return Vec3.FORWARD;
  const next = points[Math.min(index + 1, points.length - 1)];
  const prev = points[Math.max(index - 1, 0)];
  const direction =
    index < points.length - 1 ? directionXZ(points[index], next) : directionXZ(prev, points[index]);
  return direction.normalize(Vec3.FORWARD);
}

function lookAheadTarget(points: Vec3[], index: number, targetDistance: number): Vec3 {
  if (points.length < 2) {
    return points[index].add(Vec3.FORWARD.scale(targetDistance));
  }
  let accumulated = 0;
  const target = Math.max(targetDistance, 1e-6);
  for (let i = index; i < points.length - 1; i++) {
    const seg = points[i].distanceTo(points[i + 1]);
    if (accumulated + seg >= target) {
      const t = seg > 1e-10 ? (target - accumulated) / seg : 0;
      return lerpVec3(points[i], points[i + 1], t);
    }
    accumulated += seg;
  }
  // 到达终点，沿最后一段水平方向 extrapolate，保持视线稳定。
  const last = points[points.length - 1];
  const prev = points[points.length - 2];
  const dir = directionXZ(prev, last);
  return last.add(dir.scale(targetDistance - accumulated));
}

function lerpVec3(a: Vec3, b: Vec3, t: number): Vec3 {
  return new Vec3(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, a.z + (b.z - a.z) * t);
}

interface FloorNormalizeOptions {
  floorBandHeight: number;
  heightTransitionThreshold: number;
  minFloorPoints: number;
  heightSmoothWindow: number;
  transitionPadding: number;
}

function normalizeFloorHeights(points: Vec3[], options: FloorNormalizeOptions): Vec3[] {
  if (points.length < options.minFloorPoints) return points.slice();

  const smoothY = smoothHeightMedian(points, options.heightSmoothWindow);
  const floors = clusterFloorHeights(smoothY, options.floorBandHeight, options.minFloorPoints);
  if (floors.length === 0) return points.slice();

  const assigned = smoothY.map((y) => nearestFloorIndex(y, floors, options.floorBandHeight * 0.5));
  const preserve = new Array(points.length).fill(false);

  for (let i = 0; i < assigned.length; i++) {
    if (assigned[i] < 0) {
      markPreserveRange(preserve, i - options.transitionPadding, i + options.transitionPadding);
    }
    if (i === 0) continue;
    const prev = assigned[i - 1];
    const curr = assigned[i];
    if (prev >= 0 && curr >= 0 && prev !== curr) {
      const heightDiff = Math.abs(floors[prev] - floors[curr]);
      if (heightDiff >= options.heightTransitionThreshold) {
        markPreserveRange(preserve, i - options.transitionPadding, i + options.transitionPadding);
      }
    }
  }

  return points.map((p, i) => {
    const floorIndex = assigned[i];
    if (preserve[i] || floorIndex < 0) return p;
    return new Vec3(p.x, floors[floorIndex], p.z);
  });
}

function smoothHeightMedian(points: Vec3[], windowSize: number): number[] {
  const radius = Math.max(0, Math.floor(windowSize / 2));
  return points.map((_, i) => {
    const values: number[] = [];
    for (let j = Math.max(0, i - radius); j <= Math.min(points.length - 1, i + radius); j++) {
      values.push(points[j].y);
    }
    return median(values);
  });
}

function clusterFloorHeights(values: number[], bandHeight: number, minPoints: number): number[] {
  const sorted = values.slice().sort((a, b) => a - b);
  const floors: number[] = [];
  let group: number[] = [];
  for (const y of sorted) {
    if (group.length === 0 || y - group[0] <= bandHeight) {
      group.push(y);
      continue;
    }
    if (group.length >= minPoints) floors.push(median(group));
    group = [y];
  }
  if (group.length >= minPoints) floors.push(median(group));
  return floors;
}

function nearestFloorIndex(y: number, floors: number[], tolerance: number): number {
  let best = -1;
  let bestDist = Infinity;
  for (let i = 0; i < floors.length; i++) {
    const d = Math.abs(y - floors[i]);
    if (d < bestDist) {
      best = i;
      bestDist = d;
    }
  }
  return bestDist <= tolerance ? best : -1;
}

function markPreserveRange(preserve: boolean[], start: number, end: number): void {
  for (let i = Math.max(0, start); i <= Math.min(preserve.length - 1, end); i++) {
    preserve[i] = true;
  }
}

function median(values: number[]): number {
  const sorted = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) * 0.5 : sorted[mid];
}

// ─── 异常值过滤 ────────────────────────────────────────────────────────────────

function filterPositionOutliers(points: Vec3[]): Vec3[] {
  if (points.length < 4) return points;
  const mid = Math.floor(points.length / 2);
  const xs = points.map((p) => p.x).sort((a, b) => a - b);
  const ys = points.map((p) => p.y).sort((a, b) => a - b);
  const zs = points.map((p) => p.z).sort((a, b) => a - b);
  const medX = xs[mid],
    medY = ys[mid],
    medZ = zs[mid];
  const madX = xs.map((v) => Math.abs(v - medX)).sort((a, b) => a - b)[mid];
  const madY = ys.map((v) => Math.abs(v - medY)).sort((a, b) => a - b)[mid];
  const madZ = zs.map((v) => Math.abs(v - medZ)).sort((a, b) => a - b)[mid];
  const threshX = Math.max(madX * 10, 5);
  const threshY = Math.max(madY * 10, 5);
  const threshZ = Math.max(madZ * 10, 5);
  const result = points.filter(
    (p) =>
      Math.abs(p.x - medX) <= threshX &&
      Math.abs(p.y - medY) <= threshY &&
      Math.abs(p.z - medZ) <= threshZ,
  );
  return result.length >= 2 ? result : points;
}

function computeBounds(points: Vec3[]): { min: Vec3; max: Vec3; size: Vec3 } {
  if (points.length === 0) {
    return { min: Vec3.ZERO, max: Vec3.ZERO, size: Vec3.ZERO };
  }
  let min = points[0];
  let max = points[0];
  for (const p of points) {
    min = new Vec3(Math.min(min.x, p.x), Math.min(min.y, p.y), Math.min(min.z, p.z));
    max = new Vec3(Math.max(max.x, p.x), Math.max(max.y, p.y), Math.max(max.z, p.z));
  }
  return { min, max, size: max.sub(min) };
}

// ─── 图像分组（复用 track.ts 的策略） ──────────────────────────────────────────

interface ImageGroup {
  cameraId: number;
  camera?: CameraIntrinsics;
  images: ColmapCameraData[];
}

function groupImagesByCameraId(
  images: ColmapCameraData[],
  cameras: CameraIntrinsics[],
): ImageGroup[] {
  const cameraMap = new Map(cameras.map((camera) => [camera.id, camera]));
  const grouped = new Map<number, ColmapCameraData[]>();
  for (const image of [...images].sort(compareColmapImages)) {
    const group = grouped.get(image.cameraId);
    if (group) {
      group.push(image);
    } else {
      grouped.set(image.cameraId, [image]);
    }
  }

  return [...grouped.entries()]
    .map(([cameraId, groupImages]) => ({
      cameraId,
      camera: cameraMap.get(cameraId),
      images: groupImages,
    }))
    .sort(compareImageGroups);
}

function selectImageGroup(groups: ImageGroup[], cameraId: number | undefined): ImageGroup | null {
  if (cameraId !== undefined) return groups.find((group) => group.cameraId === cameraId) ?? null;
  const strategies: Record<number, number> = {
    1: 0,
    2: 0,
    3: 1,
  };
  const defaultGroupIndex = strategies[groups.length] ?? 0;
  return groups[defaultGroupIndex] ?? groups[0] ?? null;
}

function compareColmapImages(a: ColmapCameraData, b: ColmapCameraData): number {
  return compareImageNames(a.name, b.name) ?? a.id - b.id;
}

function compareImageGroups(a: ImageGroup, b: ImageGroup): number {
  return b.images.length - a.images.length || a.cameraId - b.cameraId;
}
